import type { ComponentProps } from 'react'
import { Card } from '@cocoonspace/ui/components/card'
import { Skeleton } from '@cocoonspace/ui/components/skeleton'
import { cn } from '@cocoonspace/ui/lib/utils'
import { useLingui } from '@lingui/react/macro'
import { MapPinIcon } from 'lucide-react'
import Image from 'next/image'
import { LocalizedLink as Link } from '~/components/localized-link'

import { Price } from '~/components/price'
import { EventDateTimeInfos } from '~/domains/events/components/molecules/event-date-time-infos'
import { EventCardFooter } from './event-card-footer'
import { EventCardSpacePreview } from './event-card-space-preview'
import type { EventItem } from '../my-events/use-all-events.hook'

const SpaceAddress = ({ event }: { event: EventItem }) => {
	const address1 = event.location?.address?.address1 ?? ''
	const postal_code = event.location?.address?.postal_code ?? ''
	const city = event.location?.address?.city ?? ''

	return (
		<div className='flex flex-row items-center truncate text-sm'>
			<MapPinIcon
				className='mr-1'
				strokeWidth={1.5}
			/>

			{`${address1}, ${postal_code} ${city}`}
		</div>
	)
}

const EventPastMessage = ({ event }: { event: EventItem }) => {
	const { t } = useLingui()

	return (
		<div className='text-muted-foreground text-sm'>
			{event.status === 'cancelled' && t`L'évènement a été annulé.`}
			{event.status === 'expired' && t`L'évènement a expiré.`}
			{event.status === 'option' && t`L'évènement est terminé.`}
			{event.status === 'replaced' && t`L'évènement a été remplacé.`}
			{event.status === 'validated' && t`L'évènement est terminé.`}
		</div>
	)
}

export const AddressAndPrice = ({
	event,
	isPast,
	showPrice,
}: {
	event: EventItem
	isPast?: boolean
	showPrice?: boolean
}) => (
	<div className='flex items-center justify-between gap-4'>
		<div className='truncate'>
			{isPast && <EventPastMessage event={event} />}
			{!isPast && <SpaceAddress event={event} />}
		</div>

		{showPrice && event.type !== 'quotation' && (
			<Price
				className='text-lg'
				price={event.totalPrice}
				taxClassName='mb-[5px]'
				taxType='ht'
			/>
		)}
	</div>
)

const LocationSection = ({
	event,
	isPast,
}: {
	event: EventItem
	isPast: boolean
}) => {
	const isBookingRequest = event.eventType === 'bookingRequest'
	const locationType = event.location?.type

	if (event.eventType === 'cocoon') {
		return (
			<div>
				<EventCardSpacePreview event={event} />

				<AddressAndPrice
					event={event}
					isPast={isPast}
					showPrice
				/>
			</div>
		)
	}

	if (isBookingRequest && locationType === 'remote') {
		return (
			<div className='min-h-[160px] flex-1 overflow-hidden rounded-lg bg-slate-200/30'>
				<Image
					src={'/images/video-conference.jpg'}
					alt='Video conference'
					width={300}
					height={80}
					className='w-full object-cover object-center'
				/>
			</div>
		)
	}
}

export const EventCard = ({
	event,
	isPast = false,
	className,
	hideActions = false,
	protectSchedulerEvent = false,
}: {
	event: EventItem
	isPast?: boolean
	className?: string
	hideActions?: boolean
	protectSchedulerEvent?: boolean
}) => (
	<Card
		data-umami-event='eventCard'
		className={cn(
			'flex flex-1 flex-col gap-4 p-4 transition-shadow has-[.event-details:hover]:shadow-[inset_0_0_0_3px_#ff4338]',
			className,
		)}
	>
		<Link
			href={
				event.location.type === 'remote'
					? event.googleCalendar.htmlLink || ''
					: `/events/${event.id}`
			}
			target={event.eventType === 'cocoon' ? undefined : '_blank'}
			className='event-details flex flex-col gap-4'
			shallow
		>
			<EventDateTimeInfos event={event} />

			<LocationSection
				event={event}
				isPast={isPast}
			/>
		</Link>

		{!hideActions && (
			<EventCardFooter
				event={event}
				isPast={isPast}
				protectSchedulerEvent={protectSchedulerEvent}
			/>
		)}
	</Card>
)

EventCard.skeleton = (props: ComponentProps<'div'>) => (
	<Card className={cn('relative max-w-[352px]', props.className)}>
		<EventDateTimeInfos.skeleton className='mb-4' />
		<EventCardSpacePreview.skeleton />
		<Skeleton className='mb-4 h-[16px] w-[200px]' />
		<EventCardFooter.skeleton />
	</Card>
)
